/* eslint-disable no-underscore-dangle */
import { createAction, PayloadAction } from '@reduxjs/toolkit';
import type { TransactionState } from '../reducers/transaction';
import type { AppThunk } from '../store/configureStore';
import { throwOnError } from './utils';
import { DebtRow } from 'admin/actions/account';

type Transactions = TransactionState['transactions'];
type TransactionsV2 = TransactionState['transactionsV2'];
type ListForm = TransactionState['listForm'];

type FetchTransactionsResponse = {
  count: number;
  next: string | null;
  prev: string | null;
  per_page: number;
  rows: {
    amount: number;
    customer_name: string;
    description: string;
    done_at: string;
    id: string;
    is_modified: boolean;
    money_amount: number;
    organization_name: string;
    point_amount: number;
    raw_point_amount: number;
    campaign_point_amount: number;
    shop_id: string;
    shop_name: string;
    terminal_id: string;
    type: 'topup' | 'payment';
    user_id: string;
  }[];
};

type FetchSummaryResponse = {
  summary: {
    payment: {
      count: number;
      money: number;
      point: number;
      raw_point: number;
      campaign_point: number;
    };
    topup: {
      count: number;
      money: number;
      point: number;
      raw_point: number;
      campaign_point: number;
    };
  };
};

type FetchCSVResponse = {
  url: string;
};

type GetExpiredBalancesResponse = {
  rows: {
    id: string;
    type: 'expire' | 'cashback' | null;
    expires_at: string;
    customer_id: string;
    money_amount: number;
    point_amount: number;
  }[];
  next: string | null;
  prev: string | null;
};

type C2CTransferAccount = {
  id: string;
  user: {
    id: string;
    name: string;
  };
};

type GetC2CTransactionsResponse = {
  rows: {
    id: string;
    sender_account: C2CTransferAccount;
    receiver_account: C2CTransferAccount;
    amount: number;
    done_at: string;
  }[];
  count: number;
  next: string | null;
  prev: string | null;
};

export type TransferParty = {
  id: string;
  name: string;
  is_merchant: boolean;
};

export type TransferWithPointDebt = {
  id: string;
  amount: number;
  money_amount: number;
  point_amount: number;
  description: string;
  done_at: string;
  point_debt: PointDebtDetail;
  sender: TransferParty;
  receiver: TransferParty;
};

type PointDebtDetail = {
  total: number;
  rows: DebtRow[];
};

export const GET_TRANSACTIONS = createAction<Transactions>('GET_TRANSACTIONS');
export const GET_TRANSACTIONSV2 = createAction<TransactionsV2>('GET_TRANSACTIONSV2');
export const GET_TRANSACTIONS_INFLATE = createAction<Transactions>('GET_TRANSACTIONS_INFLATE');
export const INIT_LIST_FORM = createAction<ListForm>('INIT_LIST_FORM');
export const UPDATE_LIST_FORM = createAction<ListForm>('UPDATE_LIST_FORM');
export const UPDATE_TRANSACTION = createAction<Transactions['rows'][number]>('UPDATE_TRANSACTION');

export const getTransactions =
  (query = ''): AppThunk =>
  (dispatch) =>
    fetch(`/api/transactions?${query}`, {
      method: 'GET',
      credentials: 'same-origin',
    })
      .then(throwOnError<Transactions>)
      .then((transactions) => dispatch(GET_TRANSACTIONS(transactions)));

export const getTransactionsV2 =
  (query = ''): AppThunk<Promise<PayloadAction<TransactionsV2>>> =>
  (dispatch) =>
    fetch(`/api/v2/transactions?${query}`, {
      method: 'GET',
      credentials: 'same-origin',
    })
      .then(throwOnError<TransactionsV2>)
      .then((transactions) => dispatch(GET_TRANSACTIONSV2(transactions)));

export const fetchTransactionsV2 = (query = '') =>
  fetch(`/api/v2/transactions?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<FetchTransactionsResponse>);

export const getTransactionsInflate =
  (query = ''): AppThunk =>
  (dispatch) =>
    fetch(`/api/transactions?${query}`, {
      method: 'GET',
      credentials: 'same-origin',
    })
      .then(throwOnError<Transactions>)
      .then((transactions) => dispatch(GET_TRANSACTIONS_INFLATE(transactions)));

export const getTransactionsByCustomerId = (id: string, query = '') =>
  fetch(`/api/transactions?customer_id=${id}&${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then((response) => response.json());

export const getTransaction =
  (id: string, moneyId: string): AppThunk =>
  async () => {
    const response = await fetch(`/api/transactions/${id}?private_money_id=${moneyId || ''}`, {
      method: 'GET',
      credentials: 'same-origin',
    });
    return response.json();
  };

const postRefund = (id: string, body: Record<string, unknown>) =>
  fetch(`/api/transactions/${id}/refund`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...body }),
    credentials: 'same-origin',
  }).then(throwOnError<TransferWithPointDebt>);

export const createRefund =
  (id: string, desc: string): AppThunk =>
  async () =>
    postRefund(id, { description: desc });

export const dryRunRefund = (id: string) => postRefund(id, { dry_run: true });

export const initListForm =
  (initState: ListForm): AppThunk =>
  (dispatch) =>
    dispatch(INIT_LIST_FORM(initState));

export const updateListForm =
  (update: ListForm): AppThunk =>
  (dispatch) =>
    dispatch(UPDATE_LIST_FORM(update));

export const getSummary =
  (query = ''): AppThunk =>
  () =>
    fetch(`/api/transactions/summary?${query}`, {
      method: 'GET',
      credentials: 'same-origin',
    }).then(throwOnError);

export const fetchSummary = (query = '') =>
  fetch(`/api/transactions/summary?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<FetchSummaryResponse>);

export const fetchTransactionsCSV = (query = '') =>
  fetch(`/api/transactions/csv?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<FetchCSVResponse>);

export const getExpiredBalances = (query = '') =>
  fetch(`/api/expired-balances?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<GetExpiredBalancesResponse>);

export const getExpiredBalancesCSV = (query = '') =>
  fetch(`/api/expired-balances/csv?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<FetchCSVResponse>);

export const getC2CTransactions = (query = '') =>
  fetch(`/api/c2c-transfers?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<GetC2CTransactionsResponse>);

export const getC2CTransactionsCSV = (query = '') =>
  fetch(`/api/c2c-transfers/csv?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<FetchCSVResponse>);
